
import React, { useState, useEffect } from 'react';
import { ArrowRightCircle, Clock, Calendar, Users, Award, BookOpen, Target, User, ChevronRight, ChevronLeft } from 'lucide-react';
//import { useNavigate } from 'react-router-dom';

const TitcPage: React.FC = () => {
    const [currentCard, setCurrentCard] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Load Razorpay script when payment modal is shown
    useEffect(() => {
        if (showPaymentModal) {
            // Create form element
            const form = document.createElement('form');

            // Create script element
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
            script.setAttribute('data-payment_button_id', 'pl_QzywoK1sIR26Ap');
            script.async = true;

            // Append script to form
            form.appendChild(script);

            // Find the payment container and append form
            const paymentContainer = document.getElementById('razorpay-payment-container');
            if (paymentContainer) {
                // Clear any existing content
                paymentContainer.innerHTML = '';
                paymentContainer.appendChild(form);
            }

            // Cleanup function
            return () => {
                if (paymentContainer) {
                    paymentContainer.innerHTML = '<div class="text-gray-600">Loading payment gateway...</div>';
                }
            };
        }
    }, [showPaymentModal]);

    //const navigate = useNavigate();


    const sessions = [
        {
            session: 1,
            date: "September 3, 2025",
            title: "Foundation of Trauma Therapy",
            description: "What is Trauma? Big T, small t, phase based approach of Trauma lens, how is Trauma Lens different than other modalities.",
            color: "from-amber-400 to-yellow-500",
            icon: "🧠"
        },
        {
            session: 2,
            date: "September 10, 2025",
            title: "Trauma Informed counselling skills",
            description: "Preventing re-traumatisation. (How to take 1st session from the trauma informed lens), Hakomi: Tracking & Contacting, Attunement, Corregulation. Presentation of skills with live demonstration.",
            color: "from-emerald-400 to-teal-500",
            icon: "🤝"
        },
        {
            session: 3,
            date: "September 17, 2025",
            title: "Role Play",
            description: "The skills of tracking, contacting, attunement, and language that prevents traumatisation will be practiced by each therapist. Followed with discussion.",
            color: "from-blue-400 to-indigo-500",
            icon: "🎭"
        },
        {
            session: 4,
            date: "September 24, 2025",
            title: "'Self' of the therapist",
            description: "Reflective session on social location, body + somatic awareness of the therapist. Activities, resourcing & check-ins that can help the therapist in each session.",
            color: "from-rose-400 to-pink-500",
            icon: "🌱"
        },
        {
            session: 5,
            date: "October 1, 2025",
            title: "Stabilization techniques",
            description: "for hypoarousal and hyperarousal to bring client back in their window of tolerance. Presentation of skills with live demonstration.",
            color: "from-orange-400 to-red-500",
            icon: "⚖️"
        },
        {
            session: 6,
            date: "October 8, 2025",
            title: "Role Play",
            description: "Each therapist will practice stabilisation techniques. Followed with discussion",
            color: "from-violet-400 to-purple/80",
            icon: "🛡️"
        },
        {
            session: 7,
            date: "October 15, 2025",
            title: "Role Play",
            description: "Each therapist will practice tracking, contacting, corregulation, stabilisation techniques and the somatic awareness of the therapist. Followed with discussion.",
            color: "from-cyan-400 to-blue-500",
            icon: "🔄"
        },
        {
            session: 8,
            date: "October 22, 2025",
            title: "Integrating Trauma Lens with your current modalities",
            description: "Debriefing on assessments and learnings of the past 2 months, Followed with Q&A.",
            color: "from-lime-400 to-green-500",
            icon: "🎯"
        }
    ]

    const coreSkills = [
        {
            title: "Preventing Re-traumatization",
            description: "Learn language, pacing, and safety checks that keep clients in their window of tolerance."
        },
        {
            title: "Trauma-Informed Integration",
            description: "Adapt your current modality - CBT, psychodynamic work, person-centered approaches - and weave in a trauma lens seamlessly."
        },
        {
            title: "Hakomi Principles",
            description: "Tracking & Contacting: Tune into subtle shifts in felt experience and guide clients to deeper awareness."
        },
        {
            title: "Attunement & Co-Regulation",
            description: "Cultivate presence and emotional resonance that helps clients feel recognized and held."
        },
        {
            title: "Stabilization Techniques",
            description: "Anchor clients back into safety when distress arises, using grounding tools that restore balance and support resilience."
        }
    ];

    const nextCard = () => {
        setCurrentCard((prev) => (prev + 1) % sessions.length);
    };

    const prevCard = () => {
        setCurrentCard((prev) => (prev - 1 + sessions.length) % sessions.length);
    };

    // Touch handlers for swipe functionality
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextCard();
        }
        if (isRightSwipe) {
            prevCard();
        }
    };

    const handleRegisterClick = () => {
        setShowPaymentModal(true);
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
    };


    return (
        <div className="min-h-screen bg-white">
            {/* Landing Section */}
            <section className="relative  min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-orange-400 to-orange-300">
                {/* Background layers */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple/10 to-purple rounded-br-[50%] md:rounded-br-[100%] z-0"></div>
                <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"></div>

                {/* Centered Content Container */}
                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 mt-48 md:mt-72">
                    <div className="flex flex-col items-center justify-center text-center space-y-6 sm:space-y-8 lg:space-y-10">

                        {/* Main Heading */}
                        <div className="space-y-4 sm:space-y-6">
                            <h1 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl text-white font-bold leading-tight">
                                Trauma-Informed Therapy Certification
                            </h1>
                            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed max-w-4xl mx-auto">
                                Transform your practice with hands-on skills for safety, connection, and healing. An 8-week journey for emerging mental health professionals.
                            </p>
                        </div>

                        {/* CTA Button */}
                        {/* CTA Button */}
                        <div className="w-full max-w-md">
                            <button
                                className="w-full bg-white text-purple-600 flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg lg:text-xl font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:bg-gray-50"
                                onClick={handleRegisterClick}
                            >
                                Register Now
                                <ArrowRightCircle className="ml-2 sm:ml-3 w-5 sm:w-6 lg:w-7 h-5 sm:h-6 lg:h-7" />
                            </button>
                        </div>

                        {/* Key Info Cards */}
                        <div className="w-full max-w-4xl">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 text-white">
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                                    <Calendar className="w-5 sm:w-6 lg:w-8 h-5 sm:h-6 lg:h-8 mx-auto mb-2" />
                                    <div className="text-xs sm:text-sm lg:text-base font-semibold">8 Weeks</div>
                                    <div className="text-xs sm:text-sm opacity-80">Sep 3 - Oct 22</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                                    <Clock className="w-5 sm:w-6 lg:w-8 h-5 sm:h-6 lg:h-8 mx-auto mb-2" />
                                    <div className="text-xs sm:text-sm lg:text-base font-semibold">Wednesdays</div>
                                    <div className="text-xs sm:text-sm opacity-80">7-8 PM</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                                    <Users className="w-5 sm:w-6 lg:w-8 h-5 sm:h-6 lg:h-8 mx-auto mb-2" />
                                    <div className="text-xs sm:text-sm lg:text-base font-semibold">Small Groups</div>
                                    <div className="text-xs sm:text-sm opacity-80">Practice & Feedback</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                                    <Award className="w-5 sm:w-6 lg:w-8 h-5 sm:h-6 lg:h-8 mx-auto mb-2" />
                                    <div className="text-xs sm:text-sm lg:text-base font-semibold">Certification</div>
                                    <div className="text-xs sm:text-sm opacity-80">Upon Completion</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-12 sm:py-16 bg-gradient-to-r from-yellow-400 to-yellow-500">
                <div className="container mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 sm:mb-8">Investment in Your Growth</h2>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-8">
                        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg max-w-sm w-full">
                            <div className="text-xl sm:text-2xl font-bold text-purple/600 mb-2">Early Bird</div>
                            <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">₹6,000</div>
                            <div className="text-sm text-gray-600 mb-4">Before August 14, 11:59 PM</div>
                            <div className="text-green-600 font-semibold">Save ₹1,500!</div>
                        </div>
                        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg max-w-sm w-full">
                            <div className="text-xl sm:text-2xl font-bold text-purple/600 mb-2">Regular</div>
                            <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">₹7,500</div>
                            <div className="text-sm text-gray-600 mb-4">From August 15 onwards</div>
                            <div className="text-red-500 font-semibold">Registration closes Aug 24</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What You'll Experience */}
            <section className="py-12 sm:py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-8 sm:mb-12">What You'll Experience</h2>
                    <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
                        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg">
                            <BookOpen className="w-10 sm:w-12 h-10 sm:h-12 text-purple/600 mb-4" />
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Live Demonstrations</h3>
                            <p className="text-gray-600">Watch each skill brought to life by your facilitator, so you can see not just what to do but how to do it.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg">
                            <Users className="w-10 sm:w-12 h-10 sm:h-12 text-purple/600 mb-4" />
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Role-Play Practice</h3>
                            <p className="text-gray-600">Step into the therapist's chair each week, practicing in small groups to build confidence and receive immediate, supportive feedback.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Skills */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-8 sm:mb-12">Core Skills You'll Master</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
                        {coreSkills.map((skill, index) => (
                            <div key={index} className="bg-gradient-to-br from-purple/50 to-purple/100 rounded-xl p-5 sm:p-6 shadow-lg">
                                <Target className="w-7 sm:w-8 h-7 sm:h-8 text-purple/600 mb-4" />
                                <h3 className="text-md md:text-xl font-bold text-gray-800 mb-3">{skill.title}</h3>
                                <p className="text-white text-md md:text-lg">{skill.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* courses cards */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16 lg:mb-20">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 mb-4 lg:mb-6">
                            Your <span className="font-bold text-indigo-600">Learning Path</span>
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                            Explore your 8-week transformation journey
                        </p>
                    </div>

                    <div className="relative max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}>

                        <div className="relative h-[450px] sm:h-[500px] md:h-[520px] lg:h-[560px]">
                            {sessions.map((session, index) => {
                                const isActive = index === currentCard;
                                const isPrev = index === (currentCard - 1 + sessions.length) % sessions.length;
                                const isNext = index === (currentCard + 1) % sessions.length;

                                let transform = 'translateX(100%) scale(0.85)';
                                let opacity = 0;
                                let zIndex = 0;

                                if (isActive) {
                                    transform = 'translateX(0) scale(1)';
                                    opacity = 1;
                                    zIndex = 30;
                                } else if (isPrev) {
                                    transform = 'translateX(-15%) scale(0.92)';
                                    opacity = 0.6;
                                    zIndex = 20;
                                } else if (isNext) {
                                    transform = 'translateX(15%) scale(0.92)';
                                    opacity = 0.6;
                                    zIndex = 20;
                                }

                                return (
                                    <div
                                        key={index}
                                        className="absolute inset-0 transition-all duration-500 ease-out cursor-pointer"
                                        style={{
                                            transform,
                                            opacity,
                                            zIndex
                                        }}
                                        onClick={() => setCurrentCard(index)}
                                    >
                                        <div className={`h-full bg-gradient-to-br ${session.color} rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 text-white shadow-2xl flex flex-col justify-between relative overflow-hidden`}>

                                            <div className="absolute top-0 right-0 w-24 sm:w-32 lg:w-40 h-24 sm:h-32 lg:h-40 bg-white/10 rounded-full -translate-y-12 sm:-translate-y-16 lg:-translate-y-20 translate-x-12 sm:translate-x-16 lg:translate-x-20"></div>
                                            <div className="absolute bottom-0 left-0 w-20 sm:w-24 lg:w-32 h-20 sm:h-24 lg:h-32 bg-white/5 rounded-full translate-y-10 sm:translate-y-12 lg:translate-y-16 -translate-x-10 sm:-translate-x-12 lg:-translate-x-16"></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8">
                                                    <div className="text-xs sm:text-sm lg:text-base font-medium bg-white/20 px-3 py-1 rounded-full">
                                                        Session {session.session}
                                                    </div>
                                                    <div className="text-2xl sm:text-3xl lg:text-4xl">{session.icon}</div>
                                                </div>
                                                <h3 className="text-xl md:text-lg lg:text-3xl font-bold mb-3 md:mb-4 lg:mb-6 leading-tight">{session.title}</h3>
                                                <p className="text-white/90 leading-relaxed text-md sm:text-lg lg:text-xl">{session.description}</p>
                                            </div>

                                            <div className="flex items-center justify-between relative z-10 pt-4 lg:pt-6">
                                                <div className="text-xs sm:text-sm lg:text-base font-medium">{session.date}</div>
                                                <div className="text-xs sm:text-sm lg:text-base bg-white/20 px-2 py-1 rounded-md">7-8 PM</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex justify-center items-center mt-8 sm:mt-12 lg:mt-16 space-x-6 sm:space-x-8 lg:space-x-12">
                        <button
                            onClick={prevCard}
                            className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all border border-gray-200 active:scale-95"
                        >
                            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-600" />
                        </button>

                        <div className="text-sm sm:text-base lg:text-lg text-gray-600 font-medium px-4">
                            {currentCard + 1} of {sessions.length}
                        </div>

                        <button
                            onClick={nextCard}
                            className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all border border-gray-200 active:scale-95"
                        >
                            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-600" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Eligibility */}
            <section className="py-12 sm:py-16 bg-yellow-400">
                <div className="container mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 sm:mb-8">Who Can Attend?</h2>
                    <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg max-w-2xl mx-auto">
                        <User className="w-10 sm:w-12 h-10 sm:h-12 text-purple/60 mx-auto mb-4" />
                        <p className="text-base sm:text-lg text-gray-800">
                            Anyone who has completed their first year of Master's in Clinical/Counselling/Applied Psychology can attend this certification program.
                        </p>
                    </div>
                </div>
            </section>


            { /* Facilitator */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-8 sm:mb-12">Meet Your Facilitator</h2>
                    <div className="max-w-4xl mx-auto bg-gradient-to-r from-purple/10 to-purple/20 rounded-xl p-6 sm:p-8 shadow-lg">
                        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8">
                            {/* Profile Image */}
                            <div className="flex-shrink-0">
                                {/* Profile Image */}
                                <div className="flex-shrink-0">
                                    <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden shadow-lg bg-gradient-to-br from-purple/20 to-purple/40 flex items-center justify-center">
                                        <img
                                            src="/images/team/ami-patel.jpeg"
                                            alt="Ami Patel - Trauma-Focused Therapist"
                                            className="w-full h-full object-cover"
                                        />
                                      
                                    </div>
                                </div>
                            </div>
                            {/* Content */}
                            <div className="flex-1 text-center lg:text-left">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Ami Patel (she/her)</h3>
                                <p className="text-base sm:text-lg text-purple/60 mb-4 sm:mb-6">Trauma-Focused Therapist</p>
                                <div className="space-y-3 sm:space-y-4 text-gray-700 text-sm sm:text-base">
                                    <p>✨ 7 years of experience working with various forms of trauma presentations in clients</p>
                                    <p>🎓 Masters in Clinical Psychology from University of Mumbai (2018)</p>
                                    <p>📚 1-year PG Diploma in Trauma Focused Therapy from TISS (2022-2023)</p>
                                </div>
                                <div className="mt-6 sm:mt-8 flex gap-4 justify-center lg:justify-start">
                                    {/* WhatsApp Link - Added pointer-events and z-index */}
                                    <a
                                        href="https://wa.me/8369852066"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-green-500 text-white p-3 rounded-full hover:bg-green-600 transition-colors shadow-lg group relative z-10 pointer-events-auto cursor-pointer"
                                        style={{ pointerEvents: 'auto' }}
                                    >
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                        </svg>
                                        <span className="sr-only">WhatsApp: +91 83698 52066</span>
                                    </a>

                                    {/* LinkedIn Link - Added pointer-events and z-index */}
                                    <a
                                        href="https://www.linkedin.com/in/ami-patel-9917611a4?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-colors shadow-lg group relative z-10 pointer-events-auto cursor-pointer"
                                        style={{ pointerEvents: 'auto' }}
                                    >
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                        </svg>
                                        <span className="sr-only">LinkedIn Profile</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 sm:py-16 bg-gradient-to-r from-teal-700 to-blue-900">
                <div className="container mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 sm:mb-8">
                        Ready to Transform Your Practice?
                    </h2>
                    <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                        Join us for this comprehensive certification and develop the skills to create safety, connection, and healing in your therapeutic work.
                    </p>
                    <div className="space-y-4 sm:space-y-6">
                        <button
                            onClick={handleRegisterClick}
                            className="bg-yellow-400 text-purple-800 px-6 sm:px-8 py-3 sm:py-4 text-lg sm:text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all hover:bg-yellow-300 inline-flex items-center w-full sm:w-auto justify-center cursor-pointer relative z-10"
                            style={{ pointerEvents: 'auto' }}
                            type="button"
                        >
                            Register Now - Closes August 24
                            <ArrowRightCircle className="ml-2 sm:ml-3 w-6 sm:w-7 h-6 sm:h-7" />
                        </button>
                        <div className="text-white/80 space-y-2 text-sm sm:text-base">
                            <p>📧 grehpraveshtherapy@gmail.com</p>
                            <p>📱 WhatsApp: 8591049845</p>
                        </div>
                    </div>
                </div>
            </section>
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-gray-800">Complete Registration</h3>
                                <button
                                    onClick={closePaymentModal}
                                    className="text-gray-500 hover:text-gray-700 text-2xl"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="text-center">
                                    <h4 className="text-lg font-semibold text-purple-600 mb-2">
                                        Trauma-Informed Therapy Certification
                                    </h4>
                                    <p className="text-gray-600 text-sm mb-4">
                                        8-week certification program
                                    </p>
                                </div>

                                {/* Razorpay Payment Container */}
                                <div id="razorpay-payment-container" className="text-center">
                                    <div className="text-gray-600">Loading payment gateway...</div>
                                </div>

                                <div className="text-xs text-gray-500 text-center mt-4">
                                    <p>Secure payment powered by Razorpay</p>
                                    <p className="mt-2">
                                        Need help? Contact us at{" "}
                                        <a href="mailto:grehpraveshtherapy@gmail.com" className="text-purple-600">
                                            grehpraveshtherapy@gmail.com
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TitcPage;
